/**
 * Overview — the engagement landing tab. What this engagement is.
 * REFACTOR-SPLIT: 351 -> 154 lines. The blockers block moved verbatim to
 * _OverviewBlockers.tsx in the same commit (VAL-OVERVIEW-1C R0, FILE_SIZE_POLICY.md:19).
 *
 * Composer: fetches the engagement and the signed-version history once,
 * then renders each block. Blockers (VAL-OVERVIEW-1B) and
 * particulars/versions/documents (VAL-OVERVIEW-1C) live in sibling
 * components under docs/FILE_SIZE_POLICY.md — see _OverviewBlockers.tsx,
 * _OverviewParticulars.tsx, _OverviewDocuments.tsx, _SnapshotHistory.tsx.
 */
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getValuationEngagement, fetchValuationSnapshots } from '@/api/valuation'
import { formatApiError } from '@/utils/httpErrors'
import { useAuth } from '@/contexts/AuthContext'
import type { ValuationEngagement } from '@/types/valuation'
import type { SnapshotHistoryRow } from '@/types/valuationB3'
import { money } from '@/utils/format'
import { formatMediumDate } from '@/utils/date'
import { nextStep } from './_nextStep'
import { ValuationStepper, runCache } from './_shared'
import { OverviewBlockers } from './_OverviewBlockers'
import { OverviewParticulars } from './_OverviewParticulars'
import { OverviewDocuments } from './_OverviewDocuments'
import { SnapshotHistory } from './_SnapshotHistory'
import { ReviewSteps } from './_ReviewSteps'
import { stepRole } from './_steps'

function formatUpdatedAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

export default function ValuationOverview() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const { user } = useAuth()

  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [snapshots, setSnapshots] = useState<SnapshotHistoryRow[] | null>(null)
  const [snapshotsError, setSnapshotsError] = useState<string | null>(null)

  useEffect(() => {
    if (!valuationId) return
    let live = true
    getValuationEngagement(valuationId)
      .then(en => { if (live) setEngagement(en) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    fetchValuationSnapshots(valuationId)
      .then(res => { if (live) setSnapshots(res.snapshots) })
      .catch((err: unknown) => { if (live) setSnapshotsError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-pal-error-border bg-pal-error-bg p-4 text-sm text-pal-error-text">{loadError}</div>
      </div>
    )
  }
  const concluded = engagement?.value_kind === 'concluded'
  const savedLow = engagement ? (concluded ? engagement.concluded_value_low : engagement.calculated_value_low) : null
  const savedHigh = engagement ? (concluded ? engagement.concluded_value_high : engagement.calculated_value_high) : null
  const savedHasBand = savedLow !== null && savedLow !== undefined && savedHigh !== null && savedHigh !== undefined
  // VAL-JOURNEY-FIX-3: a run lives in sessionStorage (runCache, _shared.tsx)
  // until Record/Finalize persists it onto the engagement, so a missing
  // saved band doesn't mean no band exists yet — check the session run too.
  const sessionRun = engagement && !savedHasBand ? runCache.load(engagement.valuation_engagement_id) : null
  const sessionHasBand = typeof sessionRun?.value_low === 'number' && typeof sessionRun?.value_high === 'number'
  const low = savedHasBand ? savedLow : sessionHasBand ? (sessionRun!.value_low as number) : null
  const high = savedHasBand ? savedHigh : sessionHasBand ? (sessionRun!.value_high as number) : null
  const hasBand = savedHasBand || sessionHasBand
  // VAL-PATH-ROUTES (P1): the same next step the left nav and dashboard card show.
  const step = engagement ? nextStep(engagement, user?.role) : null
  const signer = stepRole(user?.role) !== 'owner'

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          {engagement ? (
            <>
              <h1 className="text-2xl font-bold text-pal-ink">{engagement.subject_company_name}</h1>
              <p className="mt-1 text-sm text-pal-ink-muted">{engagement.industry_label ?? engagement.industry ?? 'Industry not set'}</p>
            </>
          ) : (
            <>
              <Skeleton className="h-8 w-64" />
              <Skeleton className="mt-2 h-4 w-40" />
            </>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {engagement?.status === 'signed_off' && (
            <Link
              to={`/valuation/${valuationId}/intake`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-pal-navy bg-pal-white px-4 py-2 text-sm font-semibold text-pal-navy hover:bg-pal-surface-subtle"
            >
              Edit
            </Link>
          )}
          {step && (
            <Link
              to={step.route}
              className="inline-flex items-center gap-1.5 rounded-lg bg-pal-navy px-4 py-2 text-sm font-semibold text-pal-white hover:bg-[#0d1f3c]/90"
            >
              Next: {step.label} &rarr;
            </Link>
          )}
        </div>
      </div>

      {engagement?.snapshot_credit_live && engagement.snapshot_credit_expires_on && (
        <p className="text-sm text-pal-ink-body">
          Snapshot credit of ${engagement.snapshot_credit_amount ?? ''} toward a full report, available until{' '}
          {formatMediumDate(engagement.snapshot_credit_expires_on)}.
        </p>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase text-pal-ink-muted">Valuation date</p>
            {engagement ? (
              <>
                <p className="mt-1 text-sm text-pal-ink">
                  {engagement.effective_date ? formatMediumDate(engagement.effective_date) : 'Not set'}
                </p>
                {engagement.period_label && (
                  <p className="mt-0.5 text-xs text-pal-ink-muted">{engagement.period_label}</p>
                )}
              </>
            ) : (
              <Skeleton className="mt-1 h-4 w-24" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase text-pal-ink-muted">Deliverable</p>
            {engagement ? (
              <p className="mt-1 text-sm text-pal-ink">
                {concluded ? 'Conclusion of value' : 'Calculation of value'}
              </p>
            ) : (
              <Skeleton className="mt-1 h-4 w-32" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase text-pal-ink-muted">Band</p>
            {!engagement ? (
              <Skeleton className="mt-1 h-4 w-28" />
            ) : hasBand ? (
              <p className="mt-1 text-sm text-pal-ink">{money(low)} – {money(high)}</p>
            ) : (
              <>
                <p className="mt-1 text-sm text-pal-ink">Not run yet</p>
                <p className="mt-1 text-xs text-pal-ink-muted">Run a valuation to see the range.</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase text-pal-ink-muted">Last edited</p>
            {engagement ? (
              <p className="mt-1 text-sm text-pal-ink">{formatUpdatedAt(engagement.updated_at)}</p>
            ) : (
              <Skeleton className="mt-1 h-4 w-36" />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg border border-pal-line bg-pal-surface-subtle p-4">
        {engagement ? (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <span className="text-pal-ink-muted">
                Industry <span className="ml-1 font-medium text-pal-ink">{engagement.industry_label ?? engagement.industry ?? 'Not set'}</span>
              </span>
              <span className="text-pal-ink-muted">
                Deliverable{' '}
                <span className="ml-1 font-medium text-pal-ink">
                  {concluded ? 'Conclusion of value' : 'Calculation of value'}
                </span>
              </span>
            </div>
            <p className="mt-2 text-xs text-pal-ink-muted">
              Industry and deliverable are fixed when the engagement is created.
            </p>
          </>
        ) : (
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-40" />
          </div>
        )}
      </div>

      {/* VAL-PATH-P7c: /review is the signer's first step — the step header and the owner's steps. */}
      {signer && valuationId && (
        <>
          <ValuationStepper current="review" engagementId={valuationId} role={user?.role} engagement={engagement} />
          <ReviewSteps id={valuationId} />
        </>
      )}

      {valuationId && (
        <OverviewBlockers
          id={valuationId}
          engagement={engagement}
          liveVersion={snapshots?.find(s => s.status === 'final')?.version ?? null}
        />
      )}

      {valuationId && (
        engagement ? (
          <OverviewParticulars engagement={engagement} />
        ) : (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Particulars</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-3">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        )
      )}

      <Card>
        <CardContent className="p-4">
          <h2 className="mb-3 text-sm font-semibold text-pal-ink">Signed records</h2>
          {snapshotsError ? (
            <div className="rounded-lg border border-pal-error-border bg-pal-error-bg p-4 text-sm text-pal-error-text">
              {snapshotsError}
            </div>
          ) : snapshots === null ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (
            <SnapshotHistory rows={snapshots} />
          )}
        </CardContent>
      </Card>

      {valuationId && <OverviewDocuments id={valuationId} />}
    </div>
  )
}
